import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tabs, Form, Input, Button, Spin, message, Modal, Row, Col } from "antd";
import { api } from "@redux/api";
import RecordAuditTimeline from "@components/Table/RecordAuditLog";
import { useSelector } from "react-redux";
import moment from "moment";
import notification2 from "@components/Notification2";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import { useAuth, AUTH_LEVEL } from "@/hooks/use-auth";
import FunctionalFormFieldsEditor from "@containers/management/FunctionalForm/FunctionalFormFieldsEditor";
import EquipmentCategoryLookup from "@containers/Lookup/EquipmentCategoryLookup";
import "./index.css";

const { TabPane } = Tabs;

const FunctionalFormDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { closeTab, refreshTab } = useKeepAliveContext();
  const canWrite = useAuth('functionform', AUTH_LEVEL.WRITE);
  const canFullAccess = useAuth('functionform', AUTH_LEVEL.FULL_ACCESS);

  const [loading, setLoading] = useState(true);
  const [readOnly, setReadOnly] = useState(true);
  const [detailData, setDetailData] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState("1");

  const userName = useSelector(state =>
    state?.Auth?.user ||
    state?.Auth?.first_name + " " + state?.Auth?.last_name
  );

  const parseFieldsValue = (value) => {
    if (!value) return [];

    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch (error) {
        console.error("Error parsing functional form fields:", error);
        return [];
      }
    }

    return Array.isArray(value) ? value : [];
  };

  useEffect(() => {
    if (id) {
      fetchDetail();
    }
  }, [id]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const response = await api.getFunctionalFormViewById(id);
      const data = response.data?.results?.[0];

      if (response.status === 200 && data) {
        const normalizedData = {
          ...data,
          fields: parseFieldsValue(data.fields),
        };

        setDetailData(normalizedData);
        form.setFieldsValue({
          ...normalizedData,
          created_date: normalizedData.created_date ? moment(normalizedData.created_date).format("YYYY-MM-DD HH:mm:ss") : "",
          modified_date: normalizedData.modified_date ? moment(normalizedData.modified_date).format("YYYY-MM-DD HH:mm:ss") : "",
        });
      } else {
        message.error("Functional form not found");
      }
    } catch (error) {
      console.error("Error fetching functional form:", error);
      message.error("Failed to load functional form details");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => setReadOnly(false);

  const handleCancel = () => {
    setReadOnly(true);
    if (detailData) {
      form.setFieldsValue({
        ...detailData,
        created_date: detailData.created_date ? moment(detailData.created_date).format("YYYY-MM-DD HH:mm:ss") : "",
        modified_date: detailData.modified_date ? moment(detailData.modified_date).format("YYYY-MM-DD HH:mm:ss") : "",
      });
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const submitValues = { ...values };
      submitValues.modifiedby_name = userName;
      const response = await api.editFunctionalFormView(id, submitValues);
      if (response.status === 200) {
        notification2.success({ message: "Success", description: "Functional form updated successfully" });
        setReadOnly(true);
        fetchDetail();
        refreshTab("/admin/functional-form");
      } else {
        notification2.error({ message: "Error", description: "Failed to update functional form" });
      }
    } catch (error) {
      console.error("Error updating functional form:", error);
      notification2.error({ message: "Error", description: "Failed to update functional form" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this functional form?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.deleteFunctionalFormView(id);
          if (response.status === 204 || response.status === 200) {
            notification2.success({ message: "Success", description: "Functional form deleted successfully" });
            refreshTab("/admin/functional-form");
            closeTab();
            navigate("/admin/functional-form");
          } else {
            notification2.error({ message: "Error", description: "Failed to delete functional form" });
          }
        } catch (error) {
          console.error("Error deleting functional form:", error);
          notification2.error({ message: "Error", description: "Failed to delete functional form" });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const renderOperationButton = () => {
    if (readOnly) {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          {canWrite && <Button type="primary" onClick={handleEdit}>
            <i className="fa-solid fa-pen-to-square pr-2"></i>Edit
          </Button>}
          {canFullAccess && <Button danger onClick={handleDelete}>
            <i className="fa-solid fa-trash pr-2"></i>Delete
          </Button>}
        </div>
      );
    } else {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk pr-2"></i>Save
          </Button>
          <Button onClick={handleCancel}>Cancel</Button>
        </div>
      );
    }
  };

  const renderFormFields = () => (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="horizontal"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      className="detailForm"
    >
      <Row gutter={[0, 0]}>
        <Col xs={24} lg={12}>
          <Form.Item label="ID" name="id">
            <Input disabled />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "This field is required" }]}>
            <Input disabled={readOnly} allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Type" name="type">
            {readOnly ? (
              <Input disabled value={detailData?.type || ""} />
            ) : (
              <EquipmentCategoryLookup value={detailData?.type} formRef={form} />
            )}
          </Form.Item>
        </Col>
        <Col xs={24} lg={24}>
          <Form.Item label="Fields" name="fields" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
            {readOnly ? (
              <FunctionalFormFieldsEditor value={detailData?.fields} formRef={{ setFieldsValue: () => {} }} readOnly={true} />
            ) : (
              <FunctionalFormFieldsEditor value={form.getFieldValue('fields')} formRef={form} readOnly={false} />
            )}
          </Form.Item>
        </Col>
        <Col xs={24} lg={24}></Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Created By" name="createdby_name">
            <span>{detailData?.createdby_name}</span>
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Created Date" name="created_date">
            <span>{detailData?.created_date ? moment(detailData.created_date).format("YYYY-MM-DD HH:mm:ss") : ""}</span>
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Modified By" name="lastModified_name">
            <span>{detailData?.lastModified_name}</span>
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Modified Date" name="modified_date">
            <span>{detailData?.modified_date ? moment(detailData.modified_date).format("YYYY-MM-DD HH:mm:ss") : ""}</span>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );

  if (loading && !detailData) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="functional-form-detail-container">
      <Spin spinning={loading}>
        <Tabs
          defaultActiveKey="1"
          destroyInactiveTabPane={true}
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          tabBarExtraContent={activeTabKey === "1" ? renderOperationButton() : null}
        >
          <TabPane
            tab={<span><i className="fa-regular fa-rectangle-list pr-2"></i>Detail</span>}
            key="1"
          >
            <div className="slds-m-around_x-small">
              <div className="px-4">
                <div className="w-full">{renderFormFields()}</div>
              </div>
            </div>
          </TabPane>
          {/* <TabPane
            tab={<span><i className="fa-solid fa-timeline pr-2"></i>Log</span>}
            key="2"
          >
            <div className="px-4">
              <RecordAuditTimeline moduleId={id} module="FunctionalForm" />
            </div>
          </TabPane> */}
        </Tabs>
      </Spin>
    </div>
  );
};

export default FunctionalFormDetail;
